import { Breadcrumbs } from "@/Components/breadcrumbs"
import { CardPreload } from "@/Components/Common/CardPreload"
import ListingTabBodies from "@/Components/shared/ListingTabBodies"
import { Tabs, TabsList } from "@/Components/ui/tabs"
import { Seo } from "@/Components/Seo"
import { useState } from "react"
import FilterSidebar from "@/Components/filter/FilterSidebar"
import ListingTabTrigger from "@/Components/Brands/ListingTabTrigger"
import ExpandableOverview from "@/Components/shared/ExpandableOverview"
import ExploreOtherBrands from "@/Components/Brands/ExploreOtherBrands"
import WatchlistEmptyState from "../../Components/shared/WishlistEmptyState"
import FilteredListingEmptyState from "../../Components/shared/FilteredListingEmptyState"
import useListingDefaultTab from "@/Hooks/useListingDefaultTab"
import SiteLayout from "@/Layouts/SiteLayout"

export default function BrandFacet({
    title,
    description,
    schemas,
    ogImage = null,
    breadcrumbs = [],
    brand,
    brands,
    facet,
    overviewLinks,
    liveAuctions = [],
    liveMeta,
    recentlySold = [],
    soldMeta,
    filterOptions,
    activeFilters,
    filterContext,
}) {
    const liveTotal = liveMeta?.total ?? 0
    const defaultTab = useListingDefaultTab()
    const [activeTab, setActiveTab] = useState(defaultTab)
    const heading = `${brand.name} ${facet.name} for Sale`
    // The LCP element: the first card of whichever list the default tab shows
    // (mirrors ListingTabBodies' live-first-with-sold-fallback ordering).
    const firstCard = (defaultTab === "sold" || liveTotal === 0
        ? recentlySold
        : liveAuctions)[0]
    return (
        <SiteLayout>
            <Seo
                title={title}
                description={description}
                image={ogImage}
                schemas={schemas}
            />
            <CardPreload src={firstCard?.gun?.photo_array?.[0]} />
            <div className="px-5 md:px-10 pt-6 max-w-[1440px] mx-auto">
                <Breadcrumbs breadcrumbs={breadcrumbs} />
            </div>
            <section className="flex flex-col w-full gap-4 p-5 md:p-10 pb-5 mx-auto max-w-[1440px]">
                <header className="flex flex-col">
                    <h1 className="text-[32px] font-bold">{heading}</h1>
                    <div className="flex flex-row gap-3">
                        {brand.established_year && (
                            <p className="text-sm font-semibold text-gray-500">
                                {`Founded: ${brand.established_year}`}
                            </p>
                        )}
                        {brand.country && (
                            <p className="text-sm font-semibold text-gray-500">
                                {`HQ: ${brand.country}`}
                            </p>
                        )}
                    </div>
                </header>
                <ExpandableOverview
                    text={facet.overview}
                    links={overviewLinks}
                />
            </section>
            <section className="w-full px-5 md:px-10 pb-16 mx-auto max-w-[1440px]">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="flex flex-col gap-4 md:flex-row md:items-start">
                        <div className="mt-2 sticky top-[105px] pt-[20px] md:pt-0 z-30 bg-white pb-2 md:self-start md:top-[120px] md:z-auto md:bg-transparent md:pb-0">
                            <div className="flex items-center gap-2 md:block">
                                <TabsList className="flex-1 h-auto border shadow-sm md:w-full md:mb-4">
                                    <ListingTabTrigger
                                        value="all"
                                        position="first"
                                    >
                                        All
                                    </ListingTabTrigger>
                                    <ListingTabTrigger value="active">
                                        Active ({liveTotal})
                                    </ListingTabTrigger>
                                    <ListingTabTrigger
                                        value="sold"
                                        position="last"
                                    >
                                        Sold
                                    </ListingTabTrigger>
                                </TabsList>
                                <FilterSidebar
                                    options={filterOptions}
                                    initial={activeFilters}
                                    context={filterContext}
                                />
                            </div>
                        </div>

                        <ListingTabBodies
                            liveAuctions={liveAuctions}
                            liveMeta={liveMeta}
                            recentlySold={recentlySold}
                            soldMeta={soldMeta}
                            liveTitle={`${brand.name} ${facet.name} Auctions`}
                            liveSubtitle={`${liveTotal} active auctions ending soon`}
                            liveEmptyMessage={`No active auctions for ${brand.name} ${facet.name}.`}
                            soldEmptyMessage={`No recently sold ${brand.name} ${facet.name} auctions.`}
                            emptyState={
                                <FilteredListingEmptyState
                                    activeFilters={activeFilters}
                                    fallback={
                                        <WatchlistEmptyState
                                            message={`No active auctions for ${brand.name} ${facet.name}.`}
                                            brandName={`${brand.name} ${facet.name}`}
                                            activeFilters={activeFilters}
                                            context={filterContext}
                                        />
                                    }
                                />
                            }
                        />
                    </div>
                </Tabs>
            </section>
            <ExploreOtherBrands brands={brands} currentBrandSlug={brand.slug} />
        </SiteLayout>
    )
}
